import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { rvApi } from '../api';
import { rulesViolationReport } from '../../../constants/routes';

function toLocalInputValue(d = new Date()) {
  const x = new Date(d);
  x.setMinutes(x.getMinutes() - x.getTimezoneOffset());
  return x.toISOString().slice(0, 16);
}

function RulesViolationHome() {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [locations, setLocations] = useState([]);
  const [residents, setResidents] = useState([]);
  const [propertyId, setPropertyId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [residentId, setResidentId] = useState('');
  const [inspectedAt, setInspectedAt] = useState(toLocalInputValue());
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    rvApi.properties()
      .then((res) => {
        setProperties(res.data);
        if (res.data.length === 1) setPropertyId(String(res.data[0].id));
      })
      .catch((e) => setError(e.response?.data?.error || 'Failed to load properties'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!propertyId) {
      setLocations([]);
      setLocationId('');
      setResidents([]);
      setContext(null);
      return;
    }
    rvApi.locations(propertyId).then((res) => setLocations(res.data)).catch(() => setLocations([]));
    setLocationId('');
    setResidentId('');
  }, [propertyId]);

  useEffect(() => {
    if (!locationId) {
      setResidents([]);
      setResidentId('');
      return;
    }
    rvApi.residents(locationId).then((res) => {
      setResidents(res.data);
      if (res.data.length === 1) setResidentId(String(res.data[0].id));
    }).catch(() => setResidents([]));
  }, [locationId]);

  useEffect(() => {
    if (!propertyId || !locationId) {
      setContext(null);
      return;
    }
    rvApi.context(propertyId, locationId)
      .then((res) => setContext(res.data))
      .catch(() => setContext(null));
  }, [propertyId, locationId]);

  const start = async (e) => {
    e.preventDefault();
    if (!propertyId) {
      setError('Select a community.');
      return;
    }
    setStarting(true);
    setError('');
    try {
      const res = await rvApi.createInspection({
        propertyId: Number(propertyId),
        locationId: locationId ? Number(locationId) : null,
        residentId: residentId ? Number(residentId) : null,
        inspectedAt: new Date(inspectedAt).toISOString(),
      });
      navigate(rulesViolationReport(res.data.id));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to report violation');
    } finally {
      setStarting(false);
    }
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleString() : '—');

  if (loading) return <p className="text-muted">Loading…</p>;

  return (
    <div>
      <h2 className="h4 mb-3">Report violation</h2>
      {error && <div className="alert alert-danger">{error}</div>}

      <form className="card" onSubmit={start}>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Community *</label>
              <select className="form-select" value={propertyId} onChange={(e) => setPropertyId(e.target.value)} required>
                <option value="">Select…</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Date of violation *</label>
              <input
                type="datetime-local"
                className="form-control"
                value={inspectedAt}
                onChange={(e) => setInspectedAt(e.target.value)}
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Site number</label>
              <select className="form-select" value={locationId} onChange={(e) => setLocationId(e.target.value)} disabled={!propertyId}>
                <option value="">— Select site —</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Primary resident or guest</label>
              <select className="form-select" value={residentId} onChange={(e) => setResidentId(e.target.value)} disabled={!locationId}>
                <option value="">— Optional —</option>
                {residents.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
          </div>
          <button type="submit" className="btn btn-primary mt-4" disabled={starting}>
            {starting ? 'Reporting…' : 'Report violation'}
          </button>
        </div>
      </form>

      {propertyId && locationId && context && (
        <div className="card mt-3">
          <div className="card-header">Site history</div>
          <div className="card-body small">
            <p className="mb-2">
              <strong>Most recent violation (due date):</strong>{' '}
              {context.mostRecentViolationDue
                ? formatDate(context.mostRecentViolationDue)
                : '—'}
            </p>
            <p className="mb-2">
              <strong>Most recent violation:</strong>{' '}
              {context.mostRecentViolationInspection
                ? formatDate(context.mostRecentViolationInspection)
                : '—'}
            </p>
            {context.previousInspections?.length > 0 ? (
              <>
                <strong>Previous violation reports</strong>
                <ul className="mb-0 mt-1">
                  {context.previousInspections.map((row) => (
                    <li key={row.id}>
                      {formatDate(row.inspected_at)} — {row.outcome || row.status}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="text-muted mb-0">No previous violation reports for this site.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default RulesViolationHome;
